import React, { useState } from "react";
import {
  Typography,
  List,
  ListItem,
  ListItemText,
  Button,
  Box,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Event, Ambassador } from "../Events";
import { format } from "date-fns";
import { fromUTC } from "../../GeneralUtil/timezoneUtil";

import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useUser } from "../../UserContext";
import ProfileBA from "../../Profiles/BA/ProfileBA";
import { ButtonControls } from "../../Components/SlidingSidebar";
import SelectBA from "../../Components/SelectBA";
import EventHeader from "../../Components/EventHeader";
import Avatar from "../../Components/Avatar";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

interface DetailsProps {
  eventData: Event;
  columns: Array<{
    key: string;
    header: string;
    render?: (value: any, rowData: Event) => React.ReactNode;
  }>;
  onClose: () => void;
}

const Details: React.FC<DetailsProps> = ({ eventData, columns, onClose }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { currentUser } = useUser();
  const [selectedAmbassadors, setSelectedAmbassadors] = useState<Ambassador[]>(
    []
  );
  const [selectedBA, setSelectedBA] = useState<{
    id: number;
    username: string;
  } | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [eventToDelete, setEventToDelete] = useState<number | null>(null);

  const formatTime = (time: string) => {
    const localTime = fromUTC(time);
    return format(new Date(`2000-01-01T${localTime}`), "h:mm aa");
  };

  const renderContent = (column: DetailsProps["columns"][0], data: Event) => {
    if (column.key === "time") {
      return (
        <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
          {`${formatTime(data.start_time)} - ${formatTime(data.end_time)}`}
        </Typography>
      );
    }

    if (column.render) {
      const rendered = column.render(data[column.key as keyof Event], data);
      if (React.isValidElement(rendered)) {
        return rendered;
      }
      return (
        <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
          {String(rendered)}
        </Typography>
      );
    }
    return (
      <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
        {String(data[column.key as keyof Event])}
      </Typography>
    );
  };

  const handleDeleteEvent = async () => {
    if (eventToDelete) {
      try {
        await axios.delete(
          `${import.meta.env.VITE_API_URL}/events/delete/${eventToDelete}`
        );
        onClose();
      } catch (error) {
        console.error("Error deleting event:", error);
      } finally {
        setDeleteConfirmOpen(false);
        setEventToDelete(null);
      }
    }
  };

  const handleBookAmbassadors = async () => {
    try {
      await axios.put(
        `${import.meta.env.VITE_API_URL}/events/events/${
          eventData.event_id
        }/book`,
        {
          ambassadorIds: selectedAmbassadors.map((amb) => amb.user_id),
        }
      );
      onClose();
    } catch (error) {
      console.error("Error booking ambassadors:", error);
    }
  };

  const handleLaunchLive = () => {
    navigate("/live", { state: { eventData } });
  };

  return (
    <Box
      sx={{
        height: "100%",
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
        marginTop: "20px",
        marginLeft: "5px",
        marginRight: "5px",
        pt: 0,
        pb: 6,
      }}
    >
      <Paper
        elevation={1}
        sx={{
          mx: 2,
          borderRadius: 1,
          backgroundColor: "background.paper",
          p: 0,
          flex: 1,
        }}
      >
        <EventHeader
          avatarUrl={eventData.account_json.account_avatar_url}
          accountName={eventData.account_json.account}
          accountAddress={`${eventData.account_json.address}, ${eventData.account_json.city}, ${eventData.account_json.state_code} ${eventData.account_json.zip}`}
          selectedDate={eventData.booking_date}
          startTime={formatTime(eventData.start_time)}
          endTime={formatTime(eventData.end_time)}
        />

        <List
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0,
            p: 2,
          }}
        >
          {columns.map((column) => (
            <ListItem key={column.key} sx={{ p: 0.5, borderRadius: 1 }}>
              <ListItemText
                primary={
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: "bold",
                      color: theme.palette.text.secondary,
                      fontSize: theme.typography.body1.fontSize,
                      mb: 0.5,
                    }}
                  >
                    {column.header}
                  </Typography>
                }
                secondary={renderContent(column, eventData)}
              />
            </ListItem>
          ))}

          {eventData?.status === "Pooling" &&
            eventData.ambassadors &&
            eventData.ambassadors.length > 0 && (
              <ListItem
                sx={{
                  p: 0.5,
                  borderRadius: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "stretch",
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: "bold",
                    mb: 0.5,
                    color: theme.palette.text.secondary,
                    fontSize: theme.typography.body1.fontSize,
                  }}
                >
                  Event Staff
                </Typography>
                <SelectBA
                  ambassadors={eventData.ambassadors}
                  selectedAmbassadors={selectedAmbassadors}
                  onAmbassadorChange={setSelectedAmbassadors}
                  onAmbassadorClick={(ambassador) =>
                    setSelectedBA({
                      id: ambassador.id,
                      username: ambassador.username,
                    })
                  }
                />
              </ListItem>
            )}

          {["Booked", "Completed"].includes(eventData?.status) &&
            eventData.selected_ba_json && (
              <ListItem
                sx={{
                  p: 0.5,
                  borderRadius: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "stretch",
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: "bold",
                    mb: 0.5,
                    color: theme.palette.text.secondary,
                    fontSize: theme.typography.body1.fontSize,
                  }}
                >
                  Talent Pool
                </Typography>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "5px" }}>
                  <div
                    style={{
                      cursor: "pointer",
                      border: "2px solid #6B20F5",
                      padding: 2,
                      borderRadius: "50%",
                      position: "relative",
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedBA({
                        id: eventData.selected_ba_json?.ba_user_id ?? 0,
                        username:
                          `${eventData.selected_ba_json?.first_name}-${eventData.selected_ba_json?.last_name}`.toLowerCase(),
                      });
                    }}
                  >
                    <Avatar
                      src={eventData.selected_ba_json.avatar_url}
                      alt={`${eventData.selected_ba_json.first_name} ${eventData.selected_ba_json.last_name}`}
                      size={30}
                      title={`${eventData.selected_ba_json.first_name} ${eventData.selected_ba_json.last_name}`}
                    />
                    <CheckCircleIcon
                      sx={{
                        position: "absolute",
                        bottom: -4,
                        right: -4,
                        color: "#6B20F5",
                        backgroundColor: "white",
                        borderRadius: "50%",
                        fontSize: 16,
                      }}
                    />
                  </div>
                </div>
              </ListItem>
            )}
        </List>
      </Paper>

      <ButtonControls>
        {/* Only Brand users can select/book ambassadors */}
        {currentUser?.role === "Brand" && selectedAmbassadors.length > 0 && (
          <Button
            variant="contained"
            onClick={handleBookAmbassadors}
            sx={{ fontSize: theme.typography.body2.fontSize }}
          >
            Book Ambassador
          </Button>
        )}

        {/* BA can access PROMORAVEN Live when event is Booked */}
        {currentUser?.role === "BA" && eventData.status === "Booked" && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleLaunchLive}
            sx={{ fontSize: theme.typography.body2.fontSize }}
          >
            PROMORAVEN Live
          </Button>
        )}

        {/* Both Brand and Account users can delete events */}
        {(currentUser?.role === "Brand" || currentUser?.role === "Account") && (
          <Button
            onClick={() => {
              setEventToDelete(eventData.event_id);
              setDeleteConfirmOpen(true);
            }}
            variant="outlined"
            color="error"
            sx={{ fontSize: theme.typography.body2.fontSize }}
          >
            Delete Event
          </Button>
        )}

        <Button
          onClick={onClose}
          variant="outlined"
          sx={{ fontSize: theme.typography.body2.fontSize }}
        >
          Close
        </Button>
      </ButtonControls>

      {selectedBA && (
        <ProfileBA
          baId={selectedBA.id}
          username={selectedBA.username}
          isModal={true}
          isOpen={true}
          onClose={() => setSelectedBA(null)}
        />
      )}

      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this event? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteEvent} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Details;
